import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MapPin } from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const HomePage = () => {
  const navigate = useNavigate();
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        setLoading(true);
        const data = await api.portfolio.getAll();
        setPortfolioData(data);
      } catch (error) {
        // Remove console.error in production
        toast({
          title: 'Error',
          description: 'Failed to load portfolio data. Please refresh the page.',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
  }, [toast]); // toast is stable from useToast hook

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading portfolio...</p>
        </div>
      </div>
    );
  }

  if (!portfolioData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Failed to load portfolio data.</p>
          <Button onClick={() => window.location.reload()} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Hero Section */}
      <section className="py-20 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="space-y-2">
                <h1 className="text-5xl font-bold text-gray-900 leading-tight">
                  {portfolioData.profile.name}
                </h1>
                <p className="text-2xl text-blue-600 font-semibold">
                  {portfolioData.profile.title}
                </p>
                <p className="text-lg text-gray-600">
                  {portfolioData.profile.tagline}
                </p>
              </div>

              <div className="flex flex-wrap gap-4 text-gray-600">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  <span>{portfolioData.profile.location}</span>
                </div>
              </div>

              {/* Key Stats */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 pt-4">
                {portfolioData.profile.stats.map((stat) => (
                  <div key={stat.label} className="text-center p-4 bg-white rounded-lg shadow-sm border border-blue-100">
                    <div className="text-3xl font-bold text-blue-600">{stat.value}</div>
                    <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="flex gap-4 pt-4">
                <Button onClick={() => navigate('/contact')} size="lg" className="bg-blue-600 hover:bg-blue-700">
                  Get In Touch
                </Button>
                <Button onClick={() => navigate('/experience')} size="lg" variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                  View Experience
                </Button>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-blue-200 rounded-full blur-3xl opacity-30"></div>
                <img
                  src={portfolioData.profile.photo}
                  alt="Luís Telles"
                  className="relative rounded-2xl shadow-2xl w-full max-w-md object-cover border-4 border-white"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
